import { jsx as _jsx } from "react/jsx-runtime";
// catmint/hooks/RouterContext — Router context provider for client-side routing
import { createContext, useState, useCallback } from "react";
export const RouterContext = createContext(null);
/**
 * Parses URL search string into a record.
 */
function parseSearch(searchString) {
    const params = {};
    const searchParams = new URLSearchParams(searchString);
    for (const [key, value] of searchParams) {
        params[key] = value;
    }
    return params;
}
/**
 * Performs a full-page reload via window.location.reload().
 * Extracted as a standalone function so tests running in a real browser
 * environment can override it (location.reload is non-configurable in
 * Chromium).
 *
 * @internal
 */
export let hardReload = () => {
    window.location.reload();
};
/**
 * Replace the hard-reload implementation.
 * Intended for testing only — avoids calling window.location.reload()
 * which is non-mockable in real browsers.
 *
 * @internal
 */
export function _setHardReload(fn) {
    const prev = hardReload;
    hardReload = fn;
    return prev;
}
/**
 * Provider that manages router state for client-side hooks.
 */
export function RouterProvider({ children, initialPathname, initialParams, }) {
    const isBrowser = typeof window !== "undefined";
    const [pathname, setPathname] = useState(() => initialPathname ?? (isBrowser ? window.location.pathname : "/"));
    const [search, setSearchState] = useState(() => (isBrowser ? parseSearch(window.location.search) : {}));
    const [params, setParams] = useState(() => initialParams ?? {});
    const [navigationState, setNavigationState] = useState("idle");
    const navigate = useCallback((url, opts) => {
        if (!isBrowser)
            return;
        setNavigationState("loading");
        try {
            const urlObj = new URL(url, window.location.origin);
            if (opts?.replace) {
                window.history.replaceState(null, "", urlObj.href);
            }
            else {
                window.history.pushState(null, "", urlObj.href);
            }
            setPathname(urlObj.pathname);
            setSearchState(parseSearch(urlObj.search));
            if (opts?.scroll !== false) {
                window.scrollTo(0, 0);
            }
            // Dispatch popstate so other listeners pick it up
            window.dispatchEvent(new PopStateEvent("popstate"));
        }
        finally {
            setNavigationState("idle");
        }
    }, [isBrowser]);
    const back = useCallback(() => {
        if (isBrowser) {
            window.history.back();
        }
    }, [isBrowser]);
    const forward = useCallback(() => {
        if (isBrowser) {
            window.history.forward();
        }
    }, [isBrowser]);
    const reload = useCallback((hard) => {
        if (!isBrowser)
            return;
        if (hard) {
            hardReload();
        }
        else {
            // Soft reload: re-fetch server data and re-render the current route
            // without a full page refresh (per PRD §15.3).
            setNavigationState("loading");
            // Dispatch a custom event so route data providers can re-fetch
            window.dispatchEvent(new CustomEvent("catmint:soft-reload"));
            // Fetch updated RSC payload for the current route
            const rscUrl = `/__catmint/rsc?path=${encodeURIComponent(window.location.pathname + window.location.search)}`;
            fetch(rscUrl)
                .then((res) => {
                if (res.ok) {
                    // Dispatch event with the response so the RSC runtime can process it
                    window.dispatchEvent(new CustomEvent("catmint:rsc-update", {
                        detail: { url: window.location.href },
                    }));
                }
            })
                .catch(() => {
                // Soft reload failed silently — page remains in current state
            })
                .finally(() => {
                setNavigationState("idle");
            });
        }
    }, [isBrowser]);
    const setSearch = useCallback((newParams) => {
        if (!isBrowser)
            return;
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(newParams)) {
            if (value !== undefined) {
                searchParams.set(key, value);
            }
        }
        const searchString = searchParams.toString();
        const newUrl = searchString
            ? `${window.location.pathname}?${searchString}`
            : window.location.pathname;
        window.history.replaceState(null, "", newUrl);
        setSearchState(newParams);
    }, [isBrowser]);
    // Listen for popstate (back/forward navigation)
    if (isBrowser) {
        // This is intentionally set up synchronously since we need it before first render.
        // React will handle deduplication.
        const handlePopState = () => {
            setPathname(window.location.pathname);
            setSearchState(parseSearch(window.location.search));
        };
        // Use a ref-based approach via a side effect
        // NOTE: In a real framework, this would use useSyncExternalStore
        // for proper concurrent mode support
        if (typeof window.__catmintPopStateRegistered === "undefined") {
            window.addEventListener("popstate", handlePopState);
            window.__catmintPopStateRegistered = true;
        }
    }
    void setParams; // params are set externally by route matching
    const contextValue = {
        state: {
            pathname,
            search,
            params,
            navigationState,
        },
        actions: {
            navigate,
            back,
            forward,
            reload,
            setSearch,
            setNavigationState,
        },
    };
    return (_jsx(RouterContext.Provider, { value: contextValue, children: children }));
}
//# sourceMappingURL=RouterContext.js.map